import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useStreetBeatChatLegacy as useStreetBeatChat } from 'shared/sdk-compat/useStreetBeatChatLegacy';
import { useConversationFlow, useThreadMetadata } from '@dev-streetbeat/chat/react';
import type { MessageInput } from '@dev-streetbeat/chat/types';
import chatStore from 'shared/store/chatStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import constraintsStore from 'shared/store/constraintsStore';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import { toast } from 'shared/components/ui/use-toast';
import { CONSTRAINTS } from 'pages/assistant/components/activity-panel/panels/constraints/constants';
import { extractImportContext } from 'pages/assistant/components/activity-panel/panels/constraints/lifecycle/importContext';
import { createLogger } from 'shared/logger';

const log = createLogger('Constraints');

export const useConstraintsMetadata = () => {
  const { createThreadAndStart, continueThread } = useStreetBeatChat();
  const { isLoading: conversationFlowLoading } = useConversationFlow();
  const { metadata: sdkThreadMetadata } = useThreadMetadata();
  const {
    pendingConstraintsMetadata,
    setPendingConstraintsMetadata,
    setConversationFlowLoading,
    setThreadMetadata,
    setMetadataThreadId,
  } = chatStore();
  const sessionLoaded = constraintProfilesStore((s) => s.loaded);
  const params = useParams();
  const currentThreadId = params.threadId;


  // Sync conversation flow loading state to global store
  useEffect(() => {
    setConversationFlowLoading(conversationFlowLoading);
  }, [conversationFlowLoading]);

  // Mirror SDK thread metadata into chatStore. The SDK propagates the full
  // metadata for thread.connected / thread.updated / thread.created (the
  // last one fixed in @dev-streetbeat/chat 16.3.1) and merges delta payloads
  // for metadata.updated. So sdkThreadMetadata is always authoritative —
  // we just copy it into our store mirror and track which thread it
  // belongs to.
  useEffect(() => {
    setThreadMetadata(sdkThreadMetadata);
    setMetadataThreadId(sdkThreadMetadata ? (currentThreadId ?? null) : null);

    // Only switch mode if [CONSTRAINTS.MODE_KEY] is explicitly present in metadata
    // No fallbacks - if the key is absent, mode stays unchanged
    const modeMetadata = sdkThreadMetadata?.[CONSTRAINTS.MODE_KEY] as { mode?: string } | undefined;
    if (currentThreadId && modeMetadata?.mode) {
      const mode = modeMetadata.mode as 'builder' | 'rebalancing' | 'diagnosis';
      activityPanelStore.getState().setConstraintsPanelMode(mode);
      if (mode === 'builder' || mode === 'rebalancing') {
        constraintsStore.getState().setMode(mode);
      }
    }

    // feeType is authoritative per-thread: always mirror THIS thread's imported
    // portfolio so a stale regime from a previously-opened portfolio cannot stick
    // (Fee Only shown as Fee on Top, and vice-versa). Guard on sdkThreadMetadata
    // so we only sync when we actually hold this thread's metadata — otherwise a
    // transient null render right after import (before metadata propagates) would
    // stomp the value JwtAuthService set at verify time. A thread with no imported
    // portfolio (builder) correctly clears feeType to null.
    const importedPortfolio = sdkThreadMetadata?.[CONSTRAINTS.IMPORTED_PORTFOLIO_KEY] as Record<string, any> | undefined;
    if (sdkThreadMetadata) {
      const feeType = (importedPortfolio?.customer_data?.feeType as 'feeOnTop' | 'feeOnly' | undefined) ?? null;
      if (activityPanelStore.getState().feeType !== feeType) {
        activityPanelStore.getState().setFeeType(feeType);
      }
    }

    // Activate the rebalancing predefiniti for this thread's plan +
    // risk_profile. Pure cache lookup — `useSessionConstraints` bulk-fetched
    // all 8 plans at login, so no backend call here. Each rebalancing
    // thread can carry a different imported portfolio plan, and this is
    // the place where the active pointer follows the thread.
    if (sessionLoaded && modeMetadata?.mode === 'rebalancing' && importedPortfolio) {
      const { financialPlan: threadPlan, riskProfile: threadRiskProfile } =
        extractImportContext(importedPortfolio);
      if (threadPlan != null) {
        constraintsStore
          .getState()
          .setActivePredefiniti('rebalancing', threadPlan, threadRiskProfile);
      }
    }
  }, [sdkThreadMetadata, currentThreadId, sessionLoaded]);

  // Handle pending constraints metadata
  useEffect(() => {
    const handlePendingConstraints = async () => {
      if (pendingConstraintsMetadata) {
        const { metadata: pendingMetadata, recapText } = pendingConstraintsMetadata;
        // Mark the sync in flight so the UI gates Salva / Invia / Reset /
        // dropdown for the duration of the SDK call. The flag tracks the
        // promise lifecycle: cleared in `finally`.
        constraintsStore.getState().setSyncInFlight(true);
        // Clear pendingSync BEFORE awaiting the SDK: URL navigation to the
        // newly-created thread (when there's no current thread) happens
        // mid-call, which re-runs useConstraintsSync with threadId set +
        // pendingSync still set, firing a redundant seedThread for the same
        // content. Safe on the existing-thread path too (continueThread
        // already carries the constraints, so no separate sync is needed).
        constraintsStore.getState().clearPendingSync();
        try {
          // Choose opener based on constraints panel mode
          const constraintsPanelMode = activityPanelStore.getState().constraintsPanelMode;
          const opener = constraintsPanelMode === 'rebalancing'
            ? CONSTRAINTS.SUBMIT_MESSAGE_REBALANCING
            : CONSTRAINTS.SUBMIT_MESSAGE;

          // Intent: user clicked Invia to submit the constraints panel.
          log.info('submitting constraints', {
            mode: constraintsPanelMode,
            thread: currentThreadId ?? 'new',
            hasRecap: !!recapText,
          });

          // When a recap exists, send TWO messages in one round-trip:
          //   1. user-visible recap (hidden_from_agent) — what the advisor sees.
          //   2. agent-visible opener (hidden) — what drives the agent's turn.
          // The agent's input history is built from get_messages_for_agent,
          // which filters hidden_from_agent rows; the user's transcript view
          // filters hidden rows. The two channels never cross-contaminate.
          // When there is no recap, fall back to the original single-message
          // behavior visible to both.
          const messages: MessageInput[] = recapText
            ? [
                {
                  role: 'user',
                  content: `${opener}\n\n${recapText}`,
                  hidden_from_agent: true,
                  metadata: { client_seeded: true },
                },
                {
                  role: 'user',
                  content: opener,
                  hidden: true,
                },
              ]
            : [
                { role: 'user', content: opener },
              ];

          if (!currentThreadId) {
            // For new threads, include the constraints mode if not already set
            const metadata = pendingMetadata?.[CONSTRAINTS.MODE_KEY]
              ? pendingMetadata
              : { ...pendingMetadata, [CONSTRAINTS.MODE_KEY]: { mode: constraintsPanelMode } };
            await createThreadAndStart(messages, metadata);
          } else {
            await continueThread(currentThreadId, messages, pendingMetadata);
          }
          // Result: server accepted the constraints turn.
          log.info('constraints submitted', { mode: constraintsPanelMode });
        } catch (error) {
          log.error('constraints submit failed', {
            mode: activityPanelStore.getState().constraintsPanelMode,
            thread: currentThreadId ?? 'new',
            error,
          });
          // Surface the failure: Invia is a user-visible action and the
          // server didn't accept the payload. The user can re-click Invia
          // to re-enqueue. (Re-throw was a no-op here — handlePendingConstraints
          // is invoked from inside a useEffect with no caller to catch.)
          toast({
            title: 'Errore di sincronizzazione',
            description:
              'L\'invio dei vincoli non è andato a buon fine. Riprova l\'operazione.',
            variant: 'destructive',
          });
        } finally {
          constraintsStore.getState().setSyncInFlight(false);
          setPendingConstraintsMetadata(null); // Clear the pending metadata
        }
      }
    };

    handlePendingConstraints();
  }, [pendingConstraintsMetadata, currentThreadId]);

  return {
    pendingConstraintsMetadata,
  };
};
